import React, { useState, useEffect } from 'react';
import AuthorFields from '../components/AuthorFields';
import CitationResult from '../components/CitationResult';
import { formatAuthorsForCitation, formatAuthorsForParenthetical, formatAuthorsForReference, formatURL } from '../utils/citationHelpers';

function SocialMediaForm() {
  const [authors, setAuthors] = useState([]);
  const [authorType, setAuthorType] = useState('personal');
  const [formData, setFormData] = useState({
    platform: 'YouTube',
    title: '',
    username: '',
    year: '',
    day: '',
    month: '',
    mediaType: 'Video',
    customMediaType: '',
    url: ''
  });
  const [result, setResult] = useState(null);
  const [liveResult, setLiveResult] = useState(null);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Genera una vista previa en tiempo real
  useEffect(() => {
    if (authors.length > 0 && formData.title && formData.year && formData.url) {
      generatePreview();
    } else {
      setLiveResult(null);
    }
  }, [authors, formData]);

  const formatDate = () => {
    const { year, month, day } = formData;
    if (!year) return '';

    if (day && month) {
      const monthNames = {
        '01': 'enero', '02': 'febrero', '03': 'marzo', '04': 'abril',
        '05': 'mayo', '06': 'junio', '07': 'julio', '08': 'agosto',
        '09': 'septiembre', '10': 'octubre', '11': 'noviembre', '12': 'diciembre'
      };
      return `${year}, ${parseInt(day)} de ${monthNames[month]}`;
    } else if (month) {
      const monthNames = {
        '01': 'enero', '02': 'febrero', '03': 'marzo', '04': 'abril',
        '05': 'mayo', '06': 'junio', '07': 'julio', '08': 'agosto',
        '09': 'septiembre', '10': 'octubre', '11': 'noviembre', '12': 'diciembre'
      };
      return `${year}, ${monthNames[month]}`;
    }
    return year;
  };

  const getMediaTypeLabel = () => {
    if (formData.mediaType === 'Personalizado') {
      return formData.customMediaType || 'Contenido';
    }
    return formData.mediaType;
  };

  const formatUsernameForReference = () => {
    if (!formData.username) return '';
    if (formData.platform === 'TikTok' || formData.platform === 'Instagram' || formData.platform === 'X (antes Twitter)') {
      return ` [@${formData.username}]`;
    }
    return '';
  };

  const generatePreview = () => {
    // Verificar si hay autores institucionales con siglas
    const institutionalAuthors = authors.filter(
      author => author.type === 'institutional' && author.abbreviation
    );

    let narrativeCitation, parentheticalCitation, narrativeSubsequent, parentheticalSubsequent;

    if (institutionalAuthors.length === 1 && (authorType === 'institutional' || authorType === 'both')) {
      // Para cita narrativa con institucional con sigla: "Nombre (SIGLA, año)"
      narrativeCitation = `${institutionalAuthors[0].name} (${institutionalAuthors[0].abbreviation}, ${formData.year})`;
      
      // Para cita parentética: "(Nombre [SIGLA], año)"
      parentheticalCitation = `(${institutionalAuthors[0].name} [${institutionalAuthors[0].abbreviation}], ${formData.year})`;
      
      // Para cita narrativa subsecuente: "SIGLA (año)"
      narrativeSubsequent = `${institutionalAuthors[0].abbreviation} (${formData.year})`;
      
      // Para cita parentética subsecuente: "(SIGLA, año)"
      parentheticalSubsequent = `(${institutionalAuthors[0].abbreviation}, ${formData.year})`;
    } else {
      // Caso normal sin autor institucional con sigla o múltiples autores
      const authorsForCitation = formatAuthorsForCitation(authors, authorType);
      const authorsForParenthetical = formatAuthorsForParenthetical(authors, authorType);
      const authorsForCitationSubsequent = formatAuthorsForCitation(authors, authorType, false);
      const authorsForParentheticalSubsequent = formatAuthorsForParenthetical(authors, authorType, false);

      narrativeCitation = `${authorsForCitation} (${formData.year})`;
      parentheticalCitation = `(${authorsForParenthetical}, ${formData.year})`;
      narrativeSubsequent = `${authorsForCitationSubsequent} (${formData.year})`;
      parentheticalSubsequent = `(${authorsForParentheticalSubsequent}, ${formData.year})`;
    }

    const citation = {
      narrative: narrativeCitation,
      parenthetical: parentheticalCitation,
      narrativeSubsequent: narrativeSubsequent,
      parentheticalSubsequent: parentheticalSubsequent
    };

    const authorsForReference = formatAuthorsForReference(authors, authorType);
    const dateFormatted = formatDate();
    const url = formatURL(formData.url);
    const usernameFormatted = formatUsernameForReference();
    const mediaTypeLabel = getMediaTypeLabel();

    let referenceText = '';
    
    if (formData.platform === 'YouTube' || formData.platform === 'Vimeo' || formData.platform === 'TedTalk') {
      referenceText = `${authorsForReference} (${dateFormatted}). <em>${formData.title}</em> [${mediaTypeLabel}]. ${formData.platform}`;
    } else if (formData.platform === 'TikTok' || formData.platform === 'Instagram') {
      referenceText = `${authorsForReference}${usernameFormatted} (${dateFormatted}). <em>${formData.title}</em> [${mediaTypeLabel}]. ${formData.platform}`;
    } else if (formData.platform === 'Facebook') {
      referenceText = `${authorsForReference} (${dateFormatted}). <em>${formData.title}</em> [Actualización de estado]. ${formData.platform}`;
    } else if (formData.platform === 'X (antes Twitter)') {
      referenceText = `${authorsForReference}${usernameFormatted} (${dateFormatted}). <em>${formData.title}</em> [Publicación de X]. X`;
    }
    
    if (url) {
      referenceText += `. ${url}`;
    } else {
      referenceText += '.';
    }

    const reference = {
      html: referenceText,
      text: referenceText.replace(/<[^>]*>/g, '')
    };

    setLiveResult({ citation, reference });
  };

  const generateCitation = () => {
    if (authors.length === 0 || !formData.title || !formData.year || !formData.url) {
      alert('Generador APA, séptima edición dice:\n\nPor favor, completa todos los campos obligatorios.');
      return;
    }

    // Verificar si hay autores institucionales con siglas
    const institutionalAuthors = authors.filter(
      author => author.type === 'institutional' && author.abbreviation
    );

    let narrativeCitation, parentheticalCitation, narrativeSubsequent, parentheticalSubsequent;

    if (institutionalAuthors.length === 1 && (authorType === 'institutional' || authorType === 'both')) {
      // Para cita narrativa con institucional con sigla: "Nombre (SIGLA, año)"
      narrativeCitation = `${institutionalAuthors[0].name} (${institutionalAuthors[0].abbreviation}, ${formData.year})`;
      
      // Para cita parentética: "(Nombre [SIGLA], año)"
      parentheticalCitation = `(${institutionalAuthors[0].name} [${institutionalAuthors[0].abbreviation}], ${formData.year})`;
      
      // Para cita narrativa subsecuente: "SIGLA (año)"
      narrativeSubsequent = `${institutionalAuthors[0].abbreviation} (${formData.year})`;
      
      // Para cita parentética subsecuente: "(SIGLA, año)"
      parentheticalSubsequent = `(${institutionalAuthors[0].abbreviation}, ${formData.year})`;
    } else {
      // Caso normal sin autor institucional con sigla o múltiples autores
      const authorsForCitation = formatAuthorsForCitation(authors, authorType);
      const authorsForParenthetical = formatAuthorsForParenthetical(authors, authorType);
      const authorsForCitationSubsequent = formatAuthorsForCitation(authors, authorType, false);
      const authorsForParentheticalSubsequent = formatAuthorsForParenthetical(authors, authorType, false);

      narrativeCitation = `${authorsForCitation} (${formData.year})`;
      parentheticalCitation = `(${authorsForParenthetical}, ${formData.year})`;
      narrativeSubsequent = `${authorsForCitationSubsequent} (${formData.year})`;
      parentheticalSubsequent = `(${authorsForParentheticalSubsequent}, ${formData.year})`;
    }

    const citation = {
      narrative: narrativeCitation,
      parenthetical: parentheticalCitation,
      narrativeSubsequent: narrativeSubsequent,
      parentheticalSubsequent: parentheticalSubsequent
    };

    const authorsForReference = formatAuthorsForReference(authors, authorType);
    const dateFormatted = formatDate();
    const url = formatURL(formData.url);
    const usernameFormatted = formatUsernameForReference();
    const mediaTypeLabel = getMediaTypeLabel();

    let referenceText = '';
    
    if (formData.platform === 'YouTube' || formData.platform === 'Vimeo' || formData.platform === 'TedTalk') {
      referenceText = `${authorsForReference} (${dateFormatted}). <em>${formData.title}</em> [${mediaTypeLabel}]. ${formData.platform}`;
    } else if (formData.platform === 'TikTok' || formData.platform === 'Instagram') {
      referenceText = `${authorsForReference}${usernameFormatted} (${dateFormatted}). <em>${formData.title}</em> [${mediaTypeLabel}]. ${formData.platform}`;
    } else if (formData.platform === 'Facebook') {
      referenceText = `${authorsForReference} (${dateFormatted}). <em>${formData.title}</em> [Actualización de estado]. ${formData.platform}`;
    } else if (formData.platform === 'X (antes Twitter)') {
      referenceText = `${authorsForReference}${usernameFormatted} (${dateFormatted}). <em>${formData.title}</em> [Publicación de X]. X`;
    }
    
    if (url) {
      referenceText += `. ${url}`;
    } else {
      referenceText += '.';
    }

    const reference = {
      html: referenceText,
      text: referenceText.replace(/<[^>]*>/g, '')
    };

    setResult({ citation, reference });
  };

  const getMediaTypeOptions = () => {
    switch (formData.platform) {
      case 'YouTube':
      case 'Vimeo':
      case 'TedTalk':
        return [
          { value: 'Video', label: 'Video' },
          { value: 'Audio', label: 'Audio' }
        ];
      case 'TikTok':
        return [
          { value: 'Video', label: 'Video' },
          { value: 'Audio', label: 'Audio' }
        ];
      case 'Instagram':
        return [
          { value: 'Fotografía', label: 'Fotografía' },
          { value: 'Video', label: 'Video' },
          { value: 'Audio', label: 'Audio' },
          { value: 'Ilustración', label: 'Ilustración' },
          { value: 'Dibujo', label: 'Dibujo' },
          { value: 'Personalizado', label: 'Personalizado' }
        ];
      case 'Facebook':
        return [
          { value: 'Actualización de estado', label: 'Actualización de estado' }
        ];
      case 'X (antes Twitter)':
        return [
          { value: 'Publicación de X', label: 'Publicación de X' }
        ];
      default:
        return [{ value: 'Video', label: 'Video' }];
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2 dark:text-gray-100">Redes sociales</h1>
        <p className="text-gray-600 dark:text-gray-400">Medios sociales</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-6 dark:bg-gray-800 dark:border-gray-700">
          <div>
            <label htmlFor="platform" className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
              Plataforma *
            </label>
            <select
              id="platform"
              name="platform"
              value={formData.platform}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
              required
            >
              <option value="Facebook">Facebook</option>
              <option value="Instagram">Instagram</option>
              <option value="TedTalk">TedTalk</option>
              <option value="TikTok">TikTok</option>
              <option value="Vimeo">Vimeo</option>
              <option value="X (antes Twitter)">X (antes Twitter)</option>
              <option value="YouTube">YouTube</option>
            </select>
          </div>

          <AuthorFields
            authors={authors}
            setAuthors={setAuthors}
            authorType={authorType}
            setAuthorType={setAuthorType}
            label="Autores/Creadores"
          />

          {(formData.platform === 'TikTok' || formData.platform === 'Instagram' || formData.platform === 'X (antes Twitter)') && (
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                Nombre de usuario *
              </label>
              <input
                type="text"
                id="username"
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                placeholder="usuario (sin @)"
                required
              />
            </div>
          )}

          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
              Título de la publicación *
            </label>
            <textarea
              id="title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
              placeholder="Incluye las primeras 20 palabras del contenido, emojis y hashtags"
              required
            />
            <p className="text-xs text-gray-500 mt-1 dark:text-gray-400">
              APA recomienda incluir las primeras 20 palabras, incluyendo emojis y hashtags
            </p>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label htmlFor="year" className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                Año *
              </label>
              <input
                type="number"
                id="year"
                name="year"
                value={formData.year}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                required
              />
            </div>
            <div>
              <label htmlFor="day" className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                Día *
              </label>
              <input
                type="number"
                id="day"
                name="day"
                value={formData.day}
                onChange={handleInputChange}
                min="1"
                max="31"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                required
              />
            </div>
            <div>
              <label htmlFor="month" className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                Mes *
              </label>
              <select
                id="month"
                name="month"
                value={formData.month}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                required
              >
                <option value="">Seleccionar</option>
                <option value="01">Enero</option>
                <option value="02">Febrero</option>
                <option value="03">Marzo</option>
                <option value="04">Abril</option>
                <option value="05">Mayo</option>
                <option value="06">Junio</option>
                <option value="07">Julio</option>
                <option value="08">Agosto</option>
                <option value="09">Septiembre</option>
                <option value="10">Octubre</option>
                <option value="11">Noviembre</option>
                <option value="12">Diciembre</option>
              </select>
            </div>
          </div>

          {formData.platform !== 'Facebook' && formData.platform !== 'X (antes Twitter)' && (
            <div>
              <label htmlFor="mediaType" className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                Tipo de contenido
              </label>
              <select
                id="mediaType"
                name="mediaType"
                value={formData.mediaType}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
              >
                {getMediaTypeOptions().map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {formData.mediaType === 'Personalizado' && (
            <div>
              <label htmlFor="customMediaType" className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                Tipo personalizado
              </label>
              <input
                type="text"
                id="customMediaType"
                name="customMediaType"
                value={formData.customMediaType}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                placeholder="ej: Carrusel, Historia, etc."
              />
            </div>
          )}

          <div>
            <label htmlFor="url" className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
              URL *
            </label>
            <input
              type="url"
              id="url"
              name="url"
              value={formData.url}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
              required
            />
          </div>

          <button
            type="button"
            onClick={generateCitation}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
          >
            Generar cita y referencia
          </button>
        </div>

        <div className="space-y-6">
          {!result && liveResult && (
            <CitationResult
              citation={liveResult.citation}
              reference={liveResult.reference}
              livePreview={true}
            />
          )}
          <CitationResult
            citation={result?.citation}
            reference={result?.reference}
          />
        </div>
      </div>
    </div>
  );
}

export default SocialMediaForm;